import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { DeleteCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { Table, TableHeader, TableBody, TableColumn, TableRow, TableCell } from "@heroui/react";
import { Chip, Button, ButtonGroup } from "@heroui/react";
import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon, UserMinusIcon, ListBulletIcon, TrashIcon, ViewColumnsIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import moment from "moment";
import { Kanban, BoardData, BoardItem } from "react-kanban-kit";

const TicketList: React.FC<any> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAssignedView = location.pathname.includes("/assigned");

  // View style state (list or kanban) - load from localStorage
  const [viewStyle, setViewStyle] = useState<"list" | "kanban">(() => {
    const saved = localStorage.getItem("ticketViewStyle");
    return (saved as "list" | "kanban") || "list";
  });

  const [openTickets, setOpenTickets] = useState<any>([]);
  const [assignedTickets, setAssignedTickets] = useState<any>([]);
  const [isFirstLoad, setIsFirstLoad] = useState<any>(true);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPageCount] = useState<number>(25);
  const [pageCount, setPageCount] = useState<number>(1);
  const [itemCount, setItemCount] = useState<number>(1);

  const [filters, setFilters] = useState<any>({});
  const [isFilterChange, setIsFilterChange] = useState(false);
  const [sortStat, setSortStat] = useState<any>({ createdAt: "desc" });

  // Filter UI states
  const [filterCategory, setFilterCategory] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<any>(null);
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [hideOldClosed, setHideOldClosed] = useState<boolean>(true);

  const categoryOptions = [
    { value: "Issue", label: "Issue" },
    { value: "Support", label: "Support" },
    { value: "Pickup", label: "Pickup" },
    { value: "Delivery", label: "Delivery" },
    { value: "SW-Bug", label: "SW-Bug" },
    { value: "SW-Feature", label: "SW-Feature" },
  ];

  const statusOptions = [
    { value: "Open", label: "Open" },
    { value: "In Progress", label: "In Progress" },
    { value: "Testing", label: "Testing" },
    { value: "Closed", label: "Closed" },
  ];

  // Build computed filters combining manual filters + UI filter dropdowns + hide old closed
  const computedFilters = (() => {
    const f: any = { ...filters };
    if (filterCategory) f["category"] = filterCategory.value;
    if (filterStatus) f["status"] = filterStatus.value;
    if (filterDateFrom || filterDateTo) {
      f["createdAt"] = {};
      if (filterDateFrom) f["createdAt"]["$gte"] = filterDateFrom;
      if (filterDateTo) f["createdAt"]["$lte"] = filterDateTo + "T23:59:59";
    }
    // By default hide tickets that are closed more than 1 month ago
    if (hideOldClosed && !filterStatus) {
      f["$or"] = [
        { status: { $ne: "Closed" } },
        { updatedAt: { $gte: moment().subtract(1, "month").toISOString() } },
      ];
    }
    return f;
  })();

  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  // Fetch Open Tickets (where assigned_cs_id is null)
  const {
    refetch: refetchOpenTickets,
    isLoading: isLoadingOpen,
    isFetching: isFetchingOpen,
  } = useQuery(
    ["_open_tickets_list"],
    () =>
      ListCommanWay(
        "/admin/ticket/list",
        { user: { $eq: null }, ...computedFilters },
        {
          page: currentPage,
          paginate: perPage,
          sort: { createdAt: -1 },
        }
      ),
    {
      enabled: !isAssignedView,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setIsFirstLoad(false);
          setOpenTickets(data?.data?.data || []);
          setCurrentPage(data.data.paginator?.currentPage);
          setPerPageCount(data.data.paginator?.perPage);
          setPageCount(data.data.paginator?.pageCount);
          setItemCount(data.data.paginator?.itemCount);
        }
      },
    }
  );

  // Fetch Assigned Tickets (my-list API)
  const {
    refetch: refetchAssignedTickets,
    isLoading: isLoadingAssigned,
    isFetching: isFetchingAssigned,
  } = useQuery(
    ["_assigned_tickets_list"],
    () =>
      ListCommanWay("/admin/ticket/my-list", computedFilters, {
        page: currentPage,
        paginate: perPage,
        sort: { createdAt: -1 },
      }),
    {
      enabled: isAssignedView,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setIsFirstLoad(false);
          setAssignedTickets(data?.data?.data || []);
          setCurrentPage(data.data.paginator?.currentPage);
          setPerPageCount(data.data.paginator?.perPage);
          setPageCount(data.data.paginator?.pageCount);
          setItemCount(data.data.paginator?.itemCount);
        }
      },
    }
  );

  // Assign Ticket Mutation
  const {
    mutate: assignTicketMutate,
    isLoading: isAssigning,
  }: {
    mutate: UseMutateFunction<any, unknown, any, unknown>;
    isLoading: boolean;
  } = useMutation(
    (ticketId: any) =>
      UpdateCommanWay({
        url: `/admin/ticket/assign/${ticketId}`,
        data: {},
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("Ticket Assigned Successfully", "success");
          refetchOpenTickets();
          refetchAssignedTickets();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Update Ticket Status Mutation
  const { mutate: updateTicket } = useMutation(({ apiUrl, data }: { apiUrl: string; data: any }) => UpdateCommanWay({ url: apiUrl, data }));

  // Release Ticket Mutation (set user = null)
  const { mutate: releaseTicketMutate, isLoading: isReleasing } = useMutation(
    (ticketId: any) =>
      UpdateCommanWay({
        url: `/admin/ticket/update/${ticketId}`,
        data: { user: null },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("Ticket Released", "success");
          refetchOpenTickets();
          refetchAssignedTickets();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Delete Ticket Mutation
  const { mutate: deleteTicketMutate, isLoading: isDeleting } = useMutation(
    (ticketId: any) =>
      DeleteCommanWay({
        url: `/admin/ticket/delete/${ticketId}`,
        data: {},
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("Ticket Deleted", "success");
          refetchOpenTickets();
          refetchAssignedTickets();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const performRefetch = useCallback(() => {
    if (isAssignedView) {
      setAssignedTickets([]);
      refetchAssignedTickets();
    } else {
      setOpenTickets([]);
      refetchOpenTickets();
    }
  }, [isAssignedView, refetchAssignedTickets, refetchOpenTickets]);

  useEffect(() => {
    if (isFilterChange) {
      setCurrentPage(1);
      performRefetch();
      setIsFilterChange(false);
    } else {
      performRefetch();
    }
  }, [currentPage, isFilterChange, performRefetch]);

  useEffect(() => {
    if (!isFirstLoad) {
      setIsFilterChange(true);
    }
  }, [filters, filterCategory, filterStatus, filterDateFrom, filterDateTo, hideOldClosed]);

  useEffect(() => {
    // Reset page when switching views
    setCurrentPage(1);
    setFilters({});
    setSortStat({});
    setFilterCategory(null);
    setFilterStatus(null);
    setFilterDateFrom("");
    setFilterDateTo("");
    setHideOldClosed(true);
    setIsFirstLoad(true);
  }, [location.pathname]);

  // Save view style to localStorage when it changes
  const handleViewStyleChange = (style: "list" | "kanban") => {
    setViewStyle(style);
    localStorage.setItem("ticketViewStyle", style);
  };

  // Kanban columns configuration
  const kanbanColumns = [
    { id: "todo", title: "To Do" },
    { id: "in_progress", title: "In Progress" },
    { id: "testing", title: "Testing" },
    { id: "completed", title: "Completed" },
  ];

  // Map ticket status to kanban column
  const getKanbanColumnId = (status: string) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "open" || statusLower === "todo" || statusLower === "to do") return "todo";
    if (statusLower === "in progress" || statusLower === "in_progress") return "in_progress";
    if (statusLower === "testing" || statusLower === "review") return "testing";
    if (statusLower === "closed" || statusLower === "resolved" || statusLower === "completed" || statusLower === "done") return "completed";
    return "todo";
  };

  // Map kanban column ID back to ticket status
  const getStatusFromColumnId = (columnId: string): string => {
    switch (columnId) {
      case "todo":
        return "Open";
      case "in_progress":
        return "In Progress";
      case "testing":
        return "Testing";
      case "completed":
        return "Closed";
      default:
        return "Open";
    }
  };

  // Prepare kanban data in BoardData format
  const getKanbanData = (): BoardData => {
    const data = isAssignedView ? assignedTickets : openTickets;

    // Create columns mapping with children (card ids)
    const columnCards: { [key: string]: string[] } = {
      todo: [],
      in_progress: [],
      testing: [],
      completed: [],
    };

    // Group tickets by column
    data.forEach((ticket: any) => {
      const columnId = getKanbanColumnId(ticket.status);
      columnCards[columnId].push(`card-${ticket.id}`);
    });

    // Build BoardData structure
    const boardData: BoardData = {
      root: {
        id: "root",
        title: "Root",
        parentId: null,
        children: kanbanColumns.map((col) => col.id),
        totalChildrenCount: kanbanColumns.length,
      },
    };

    // Add columns to boardData
    kanbanColumns.forEach((col) => {
      boardData[col.id] = {
        id: col.id,
        title: col.title,
        parentId: "root",
        children: columnCards[col.id],
        totalChildrenCount: columnCards[col.id].length,
        type: "column",
      };
    });

    // Add cards to boardData
    data.forEach((ticket: any) => {
      const columnId = getKanbanColumnId(ticket.status);
      const cardId = `card-${ticket.id}`;
      boardData[cardId] = {
        id: cardId,
        title: ticket.subject || ticket.ticket_number || `Ticket #${ticket.id}`,
        parentId: columnId,
        children: [],
        totalChildrenCount: 0,
        type: "card",
        content: ticket,
      };
    });

    return boardData;
  };

  const getCategotyColor = (category: any): "primary" | "warning" | "secondary" | "success" | "default" | "danger" => {
    switch (category?.toLowerCase()) {
      case "sw-bug":
        return "danger";
      case "sw-feature":
        return "warning";
      case "support":
        return "primary";
      case "issue":
        return "secondary";
      default:
        return "default";
    }
  };
  // Kanban config map for rendering cards
  const kanbanConfigMap = {
    column: {
      render: () => null,
      isDraggable: false,
    },
    card: {
      render: ({ data: card }: { data: BoardItem; column: BoardItem; index: number; isDraggable: boolean }) => (
        <div className="bg-white px-2 py-1.5 rounded border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow">
          <div className="flex items-center justify-between gap-1">
            <span className="text-[11px] text-gray-400 shrink-0">{card.content?.ticket_number || `#${card.content?.id}`}</span>
            <div className="flex items-center gap-1">
              <Chip color={getCategotyColor(card.content?.category)} size="sm" variant="solid" className="h-4 text-[10px]">
                {card.content?.category}
              </Chip>
              <Chip color={getPriorityColor(card.content?.priority)} size="sm" variant="flat" className="h-4 text-[10px]">
                {card.content?.priority || "Med"}
              </Chip>
            </div>
          </div>
          <p className="text-xs font-medium text-gray-800 line-clamp-1 mt-0.5">{card.title || "No Subject"}</p>
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[10px] text-gray-400">
              {card.content?.createdAt && moment(card.content.createdAt).isValid()
                ? moment(card.content.createdAt).format("DD MMM")
                : ""}
            </span>
            {!isAssignedView && (
              <Button
                size="sm"
                color="primary"
                variant="flat"
                className="text-[10px] h-5 min-w-0 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePickTicket(card.content?.id);
                }}
              >
                Pick
              </Button>
            )}
          </div>
        </div>
      ),
      isDraggable: true,
    },
  };

  const handleSort = (key: string) => {
    let data = isAssignedView ? [...assignedTickets] : [...openTickets];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    let sortedData = data.sort((a, b) => {
      let valueA = key.split(".").reduce((obj, k) => obj?.[k], a) || "";
      let valueB = key.split(".").reduce((obj, k) => obj?.[k], b) || "";

      if (["createdAt", "updatedAt"].includes(key)) {
        const dateA = moment(valueA);
        const dateB = moment(valueB);
        if (!dateA.isValid() || !dateB.isValid()) return 0;
        return order === "asc" ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);

    if (isAssignedView) {
      setAssignedTickets(sortedData);
    } else {
      setOpenTickets(sortedData);
    }
  };

  const GET_SORT_ICON = (key: string) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
    }
  };

  const getPriorityColor = (priority: any): "danger" | "warning" | "primary" | "default" => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: any): "primary" | "warning" | "secondary" | "success" | "default" => {
    switch (status?.toLowerCase()) {
      case "open":
        return "primary";
      case "in progress":
        return "warning";
      case "pending":
        return "secondary";
      case "closed":
        return "success";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  const handlePickTicket = (ticketId: any) => {
    if (window.confirm("Do you want to assign this ticket to yourself?")) {
      assignTicketMutate(ticketId);
    }
  };

  const handleReleaseTicket = (ticketId: any) => {
    if (window.confirm("Release this ticket? It will be unassigned.")) {
      releaseTicketMutate(ticketId);
    }
  };

  const handleDeleteTicket = (ticketId: any) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      deleteTicketMutate(ticketId);
    }
  };

  const handleViewDetails = (ticketId: any) => {
    navigate(`/tickets/detail/${ticketId}`);
  };

  const tableData = isAssignedView ? assignedTickets : openTickets;
  const isLoading = isAssignedView ? isLoadingAssigned : isLoadingOpen;
  const isFetching = isAssignedView ? isFetchingAssigned : isFetchingOpen;

  return (
    <>
      {(isLoading || isAssigning || isReleasing || isDeleting) && <Spinner />}

      <div className="flex justify-between items-center px-4 mb-2 mt-2">
        <h1 className="text-lg font-bold font-inter underline">{isAssignedView ? "My Assigned Tickets" : "Open Tickets"}</h1>
        <div className="flex gap-4 items-center">
          {/* New Ticket Button */}
          <Button size="sm" color="success" variant="solid" onClick={() => navigate("/tickets/detail/new")}>
            + New Ticket
          </Button>

          {/* View Style Toggle */}
          <ButtonGroup size="sm">
            <Button
              color={viewStyle === "list" ? "primary" : "default"}
              variant={viewStyle === "list" ? "solid" : "bordered"}
              onClick={() => handleViewStyleChange("list")}
              startContent={<ListBulletIcon className="h-4 w-4" />}
            >
              List
            </Button>
            <Button
              color={viewStyle === "kanban" ? "primary" : "default"}
              variant={viewStyle === "kanban" ? "solid" : "bordered"}
              onClick={() => handleViewStyleChange("kanban")}
              startContent={<ViewColumnsIcon className="h-4 w-4" />}
            >
              Kanban
            </Button>
          </ButtonGroup>

          {/* Ticket Type Toggle */}
          <ButtonGroup size="sm">
            <Button
              color={!isAssignedView ? "primary" : "default"}
              variant={!isAssignedView ? "solid" : "bordered"}
              onClick={() => navigate("/tickets/open")}
            >
              Open Tickets
            </Button>
            <Button
              color={isAssignedView ? "primary" : "default"}
              variant={isAssignedView ? "solid" : "bordered"}
              onClick={() => navigate("/tickets/assigned")}
            >
              My Tickets
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className="px-4">
        {/* Filter Bar */}
        <div className="flex flex-wrap gap-3 items-end mb-3 p-3 bg-gray-50 rounded-lg border relative z-20">
          <div className="min-w-[150px]">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
            <Select
              isClearable
              placeholder="All"
              options={categoryOptions}
              value={filterCategory}
              onChange={(val) => setFilterCategory(val)}
              menuPortalTarget={document.body}
              styles={{
                control: (b) => ({ ...b, minHeight: 32, fontSize: 13 }),
                menu: (b) => ({ ...b, fontSize: 13 }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="min-w-[150px]">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
            <Select
              isClearable
              placeholder="All"
              options={statusOptions}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              menuPortalTarget={document.body}
              styles={{
                control: (b) => ({ ...b, minHeight: 32, fontSize: 13 }),
                menu: (b) => ({ ...b, fontSize: 13 }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
          </div>
          <div>
            <AfDateRangePicker
              setStartDate={setFilterDateFrom}
              setEndDate={setFilterDateTo}
              label="Date Range"
              startDate={filterDateFrom || undefined}
              endDate={filterDateTo || undefined}
            />
          </div>
          <div className="flex items-center gap-2 h-[32px]">
            <input
              type="checkbox"
              id="hideOldClosed"
              checked={hideOldClosed}
              onChange={(e) => setHideOldClosed(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="hideOldClosed" className="text-xs text-gray-600 whitespace-nowrap">Hide closed (&gt;1 month)</label>
          </div>
          <Button
            size="sm"
            color="default"
            variant="bordered"
            onClick={() => {
              setFilterCategory(null);
              setFilterStatus(null);
              setFilterDateFrom("");
              setFilterDateTo("");
              setHideOldClosed(true);
              setFilters({});
            }}
          >
            Clear
          </Button>
        </div>
        {/* List View */}
        {viewStyle === "list" && (
          <>
            <div className="flex font-inter">
              <Table
                isStriped
                isHeaderSticky
                removeWrapper
                className="w-full text-sm text-left text-gray-500"
                aria-label="tickets table"
                classNames={{
                  td: "group-data-[odd=true]/tr:before:bg-blue-100",
                }}
              >
                <TableHeader className="text-gray-700 uppercase bg-gray-50 sticky top-0">
                  <TableColumn className="text-sm px-2 py-1 text-center">ID {GET_SORT_ICON("id")}</TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Ticket No {GET_SORT_ICON("ticket_number")}
                    <input
                      type="search"
                      className="block p-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 mx-auto z-50"
                      placeholder="Search"
                      onKeyDown={(event: any) => {
                        if (event.key === "Enter") {
                          let _filters = { ...filters };
                          let val = event.target?.value;
                          if (!val) {
                            delete _filters["ticket_number"];
                          } else {
                            _filters["ticket_number"] = { $like: `%${val}%` };
                          }
                          setFilters(_filters);
                        }
                      }}
                    />
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Subject {GET_SORT_ICON("subject")}
                    <input
                      type="search"
                      className="block p-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 mx-auto z-50"
                      placeholder="Search"
                      onKeyDown={(event: any) => {
                        if (event.key === "Enter") {
                          let _filters = { ...filters };
                          let val = event.target?.value;
                          if (!val) {
                            delete _filters["subject"];
                          } else {
                            _filters["subject"] = { $like: `%${val}%` };
                          }
                          setFilters(_filters);
                        }
                      }}
                    />
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Category {GET_SORT_ICON("category")}
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Priority {GET_SORT_ICON("priority")}
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Status {GET_SORT_ICON("status")}
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                    Created {GET_SORT_ICON("createdAt")}
                  </TableColumn>
                  <TableColumn className="text-sm px-2 py-1 text-center">Actions</TableColumn>
                </TableHeader>

                <TableBody>
                  {!tableData.length && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center">
                        {isFetching ? "Loading..." : "No tickets found"}
                      </TableCell>
                    </TableRow>
                  )}
                  {tableData.map((ticket: any, j: number) => {
                    return (
                      <TableRow key={ticket.id}>
                        <TableCell className="text-center">{ticket.id}</TableCell>
                        <TableCell className="text-center">{ticket.ticket_number || "-"}</TableCell>
                        <TableCell className="text-left px-2">{ticket.subject || "-"}</TableCell>
                        <TableCell className="text-center">
                          <Chip color={getCategotyColor(ticket.category)} size="sm" variant="solid">
                            <span className="font-semibold px-2">{ticket.category || "-"}</span>
                          </Chip>
                        </TableCell>
                        <TableCell className="text-center">
                          <Chip color={getPriorityColor(ticket.priority)} size="sm" variant="flat">
                            <span className="font-semibold px-2">{ticket.priority || "Medium"}</span>
                          </Chip>
                        </TableCell>
                        <TableCell className="text-center">
                          <Chip color={getStatusColor(ticket.status)} size="sm" variant="flat">
                            <span className="font-semibold px-2">{ticket.status || "Open"}</span>
                          </Chip>
                        </TableCell>
                        <TableCell className="text-center">
                          {ticket.createdAt || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center">
                            {!isAssignedView ? (
                              <Button size="sm" color="primary" variant="solid" onClick={() => handlePickTicket(ticket.id)}>
                                Pick
                              </Button>
                            ) : (
                              <>
                                <Button size="sm" color="primary" variant="solid" onClick={() => handleViewDetails(ticket.id)}>
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  color="warning"
                                  variant="flat"
                                  isIconOnly
                                  title="Release Ticket"
                                  onClick={() => handleReleaseTicket(ticket.id)}
                                >
                                  <UserMinusIcon className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              color="danger"
                              variant="flat"
                              isIconOnly
                              title="Delete Ticket"
                              onClick={() => handleDeleteTicket(ticket.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <nav className="flex items-center justify-between pt-4" aria-label="Table navigation">
              <span className="text-sm font-normal text-gray-500">
                Total: <span className="font-semibold text-gray-900">{itemCount}</span>
              </span>
              <ul className="inline-flex -space-x-px text-sm h-8">
                <li>
                  <div
                    onClick={() => setCurrentPage((prevData) => (prevData > 1 ? prevData - 1 : prevData))}
                    className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                  >
                    Previous
                  </div>
                </li>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                  <li key={"paginate-" + p}>
                    <div
                      onClick={() => setCurrentPage(p)}
                      className={`flex items-center justify-center px-3 h-8 leading-tight cursor-pointer ${
                        currentPage === p
                          ? "text-blue-600 border border-gray-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                      }`}
                    >
                      {p}
                    </div>
                  </li>
                ))}
                <li>
                  <div
                    onClick={() => setCurrentPage((prevData) => (prevData < pageCount ? prevData + 1 : prevData))}
                    className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                  >
                    Next
                  </div>
                </li>
              </ul>
            </nav>
          </>
        )}

        {/* Kanban View */}
        {viewStyle === "kanban" && (
          <div className="font-inter kanban-container min-h-[500px]">
            <Kanban
              dataSource={getKanbanData()}
              configMap={kanbanConfigMap}
              renderColumnHeader={(column) => (
                <div className="flex items-center justify-between px-2 py-2 rounded-t-lg">
                  <h3 className="font-semibold text-gray-700">{column.title}</h3>
                  <span className=" bg-gray-400 text-white text-xs font-medium px-2 py-1 rounded-full">{column.children.length}</span>
                </div>
              )}
              columnWrapperClassName={() => "min-w-fit max-w-full rounded-lg min-h-[400px]"}
              rootClassName="grid grid-cols-4 gap-4 pb-4"
              cardsGap={4}
              onCardMove={({ cardId, fromColumnId, toColumnId }) => {
                if (fromColumnId === toColumnId) return;

                const ticketId = cardId.replace("card-", "");
                const newStatus = getStatusFromColumnId(toColumnId);

                // Update ticket status via API
                updateTicket(
                  {
                    apiUrl: `/admin/ticket/update/${ticketId}`,
                    data: { status: newStatus },
                  },
                  {
                    onSuccess: () => {
                      UseToast("Ticket status updated", "success");
                      performRefetch();
                    },
                    onError: (error: any) => {
                      UseToast(error?.message || "Failed to update status", "error");
                      performRefetch(); // Refresh to revert the UI
                    },
                  }
                );
              }}
              onCardClick={(e, card) => {
                handleViewDetails(card.content?.id);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TicketList;
